import React, { useState, useEffect, useRef } from 'react';
import { User, GenerationTemplate, VideoModel, ImageModel, LLMProvider, LLMRoleConfigMap, SkillStatus } from '../types';
import {
  listGenerationTemplates,
  createGenerationTemplate,
  updateGenerationTemplate,
  deleteGenerationTemplate,
  uploadTemplateRefImage,
  deleteTemplateRefImage,
  uploadTemplateCoverImage,
  deleteTemplateCoverImage,
  listActiveVideoModels,
  listActiveImageModels,
  listLLMProvidersPublic,
  submitGenerationTemplate,
  withdrawGenerationTemplate,
  approveGenerationTemplate,
  rejectGenerationTemplate,
  publishGenerationTemplate,
  unpublishGenerationTemplate,
} from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import HelpTip from '../components/HelpTip';
import { useToast } from '../components/Toast';
import { tosAccelerate } from '../utils/tosAccelerate';
import { hasPermission } from '../utils/permissions';
import { useTranslation } from 'react-i18next';
import { buildLLMRoleOverrideOptions } from './project-detail/components/llmRoleOverrideOptions';
import LLMRoleOverridesSection from './project-detail/components/LLMRoleOverridesSection';
import { MAX_TOTAL_REF_IMAGES } from './project-detail/utils/editDefaults';
import { useImageUploadZone, type UseImageUploadZoneResult } from '../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../utils/imageFiles';

const statusLabels: Record<SkillStatus, { text: string; color: string; bg: string }> = {
  draft: { text: 'draft', color: 'var(--text-2)', bg: 'var(--surface-2)' },
  pending_review: { text: 'pending_review', color: '#b45309', bg: '#fef3c7' },
  published: { text: 'published', color: '#15803d', bg: '#dcfce7' },
  unpublished: { text: 'unpublished', color: '#dc2626', bg: '#fee2e2' },
};

const parseRefImages = (s?: string): string[] => {
  try { return JSON.parse(s || '[]') as string[]; } catch { return []; }
};

const DEFAULT_FIXED_PROMPT = '参考图片来生成图片';
const DEFAULT_CHAR_STATE_PREFIX = '参考图1 的人物，根据下面描述生成，规则：单图包含头部单独正面视图+全身三视图(正面/侧面/背面),从左到右排列,纯白色背景,布局清晰无重叠、比例协调,视图间预留均匀空白;描述：';

interface FormState {
  name: string;
  isDefault: boolean;
  jimengModel: string;
  imagePlatform: string;
  imageModel: string;
  baseImagePlatform: string;
  baseImageModel: string;
  videoPlatform: string;
  videoModel: string;
  llmRoleOverrides: Partial<LLMRoleConfigMap>;
  charFixedPrompt: string;
  charProjectRefImages: string[];
  sceneFixedPrompt: string;
  sceneProjectRefImages: string[];
  propFixedPrompt: string;
  propProjectRefImages: string[];
  charStatePromptPrefix: string;
  sceneStatePromptPrefix: string;
  propStatePromptPrefix: string;
  charStateImageRatio: string;
  sceneStateImageRatio: string;
  propStateImageRatio: string;
  videoPrefixPrompt: string;
  videoExtraPrompt: string;
  coverImageUrl: string;
}

const defaultForm = (): FormState => ({
  name: '',
  isDefault: false,
  jimengModel: 'jimeng-5.0',
  imagePlatform: 'moyu-jimeng',
  imageModel: 'jimeng-5.0',
  baseImagePlatform: '',
  baseImageModel: '',
  videoPlatform: 'moyu-jimeng',
  videoModel: 'seedance-2.0-pro',
  llmRoleOverrides: {},
  charFixedPrompt: DEFAULT_FIXED_PROMPT,
  charProjectRefImages: [],
  sceneFixedPrompt: DEFAULT_FIXED_PROMPT,
  sceneProjectRefImages: [],
  propFixedPrompt: DEFAULT_FIXED_PROMPT,
  propProjectRefImages: [],
  charStatePromptPrefix: DEFAULT_CHAR_STATE_PREFIX,
  sceneStatePromptPrefix: '',
  propStatePromptPrefix: '',
  charStateImageRatio: '16:9',
  sceneStateImageRatio: '16:9',
  propStateImageRatio: '16:9',
  videoPrefixPrompt: '',
  videoExtraPrompt: '',
  coverImageUrl: '',
});

const TemplateList: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation('skills');
  const guardAspectRatio = useImageAspectRatioGuard();
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const canModerate = hasPermission(currentUser, 'operations');       // 审核/查看全部/管理员视图/published
  const canEditDefault = hasPermission(currentUser, 'platform_config'); // default 资产编辑
  const canEdit = (tpl: GenerationTemplate) => canModerate || tpl.createdBy === currentUser?.id;
  const isOwner = (tpl: GenerationTemplate) => !!currentUser && tpl.createdBy === currentUser.id;
  const [templates, setTemplates] = useState<GenerationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<GenerationTemplate | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingTemplate, setDeletingTemplate] = useState<GenerationTemplate | null>(null);

  // reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<GenerationTemplate | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [activeVideoModels_, setActiveVideoModels] = useState<VideoModel[]>([]);
  const [activeImageModels_, setActiveImageModels] = useState<ImageModel[]>([]);
  const [llmProviders, setLLMProviders] = useState<LLMProvider[]>([]);
  const charFileRef = useRef<HTMLInputElement>(null!);
  const sceneFileRef = useRef<HTMLInputElement>(null!);
  const propFileRef = useRef<HTMLInputElement>(null!);
  const coverFileRef = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    load();
    listActiveVideoModels().then(d => setActiveVideoModels(d || [])).catch(() => {});
    listActiveImageModels().then(d => setActiveImageModels(d || [])).catch(() => {});
    listLLMProvidersPublic().then(d => setLLMProviders(d || [])).catch(() => {});
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listGenerationTemplates();
      setTemplates(data || []);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setForm(defaultForm());
    setError('');
    setShowModal(true);
  };

  const openEdit = (tpl: GenerationTemplate) => {
    setEditingTemplate(tpl);
    setForm({
      name: tpl.name,
      isDefault: tpl.isDefault,
      jimengModel: tpl.jimengModel || 'jimeng-5.0',
      imagePlatform: tpl.imagePlatform || 'moyu-jimeng',
      imageModel: tpl.imageModel || tpl.jimengModel || 'jimeng-5.0',
      baseImagePlatform: tpl.baseImagePlatform || '',
      baseImageModel: tpl.baseImageModel || '',
      videoPlatform: tpl.videoPlatform || 'moyu-jimeng',
      videoModel: tpl.videoModel || 'seedance-2.0-pro',
      llmRoleOverrides: tpl.llmRoleOverrides || {},
      charFixedPrompt: tpl.charFixedPrompt || DEFAULT_FIXED_PROMPT,
      charProjectRefImages: parseRefImages(tpl.charProjectRefImages),
      sceneFixedPrompt: tpl.sceneFixedPrompt || DEFAULT_FIXED_PROMPT,
      sceneProjectRefImages: parseRefImages(tpl.sceneProjectRefImages),
      propFixedPrompt: tpl.propFixedPrompt || DEFAULT_FIXED_PROMPT,
      propProjectRefImages: parseRefImages(tpl.propProjectRefImages),
      charStatePromptPrefix: tpl.charStatePromptPrefix || DEFAULT_CHAR_STATE_PREFIX,
      sceneStatePromptPrefix: tpl.sceneStatePromptPrefix || '',
      propStatePromptPrefix: tpl.propStatePromptPrefix || '',
      charStateImageRatio: tpl.charStateImageRatio || '16:9',
      sceneStateImageRatio: tpl.sceneStateImageRatio || '16:9',
      propStateImageRatio: tpl.propStateImageRatio || '16:9',
      videoPrefixPrompt: tpl.videoPrefixPrompt || '',
      videoExtraPrompt: tpl.videoExtraPrompt || '',
      coverImageUrl: tpl.coverImageUrl || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t('common.nameRequired')); return; }
    setSaving(true);
    setError('');
    try {
      const payload: Partial<GenerationTemplate> = {
        name: form.name.trim(),
        isDefault: form.isDefault,
        jimengModel: form.jimengModel,
        imagePlatform: form.imagePlatform,
        imageModel: form.imageModel,
        baseImagePlatform: form.baseImagePlatform,
        baseImageModel: form.baseImageModel,
        videoPlatform: form.videoPlatform,
        videoModel: form.videoModel,
        llmRoleOverrides: form.llmRoleOverrides,
        charFixedPrompt: form.charFixedPrompt,
        charProjectRefImages: JSON.stringify(form.charProjectRefImages),
        sceneFixedPrompt: form.sceneFixedPrompt,
        sceneProjectRefImages: JSON.stringify(form.sceneProjectRefImages),
        propFixedPrompt: form.propFixedPrompt,
        propProjectRefImages: JSON.stringify(form.propProjectRefImages),
        charStatePromptPrefix: form.charStatePromptPrefix,
        sceneStatePromptPrefix: form.sceneStatePromptPrefix,
        propStatePromptPrefix: form.propStatePromptPrefix,
        charStateImageRatio: form.charStateImageRatio,
        sceneStateImageRatio: form.sceneStateImageRatio,
        propStateImageRatio: form.propStateImageRatio,
        videoPrefixPrompt: form.videoPrefixPrompt,
        videoExtraPrompt: form.videoExtraPrompt,
      };
      if (editingTemplate) {
        const updated = await updateGenerationTemplate(editingTemplate.id, payload);
        setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? updated : t));
      } else {
        const created = await createGenerationTemplate(payload);
        setTemplates(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (tpl: GenerationTemplate) => {
    setDeletingTemplate(tpl);
  };

  const confirmDelete = async () => {
    if (!deletingTemplate) return;
    try {
      await deleteGenerationTemplate(deletingTemplate.id);
      setTemplates(prev => prev.filter(x => x.id !== deletingTemplate.id));
    } catch (err: any) {
      toast.error(err.message || t('common.deleteFailed'));
    } finally {
      setDeletingTemplate(null);
    }
  };

  const handleSubmitReview = async (tpl: GenerationTemplate) => {
    try {
      const updated = await submitGenerationTemplate(tpl.id);
      setTemplates(prev => prev.map(x => x.id === tpl.id ? updated : x));
      toast.success(t('common.submitReviewSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.submitFailed'));
    }
  };

  const handleWithdraw = async (tpl: GenerationTemplate) => {
    try {
      const updated = await withdrawGenerationTemplate(tpl.id);
      setTemplates(prev => prev.map(x => x.id === tpl.id ? updated : x));
      toast.success(t('common.withdrawSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.withdrawFailed'));
    }
  };

  const handleApprove = async (tpl: GenerationTemplate) => {
    try {
      const updated = await approveGenerationTemplate(tpl.id);
      setTemplates(prev => prev.map(x => x.id === tpl.id ? updated : x));
      toast.success(t('common.approveSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const openReject = (tpl: GenerationTemplate) => {
    setRejectTarget(tpl);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    if (!rejectNote.trim()) { toast.warning(t('common.rejectNoteRequired')); return; }
    try {
      const updated = await rejectGenerationTemplate(rejectTarget.id, rejectNote);
      setTemplates(prev => prev.map(x => x.id === rejectTarget.id ? updated : x));
      toast.success(t('common.rejected'));
      setShowRejectModal(false);
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handlePublish = async (tpl: GenerationTemplate) => {
    try {
      const updated = await publishGenerationTemplate(tpl.id);
      setTemplates(prev => prev.map(x => x.id === tpl.id ? updated : x));
      toast.success(t('common.republished'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handleUnpublish = async (tpl: GenerationTemplate) => {
    try {
      const updated = await unpublishGenerationTemplate(tpl.id);
      setTemplates(prev => prev.map(x => x.id === tpl.id ? updated : x));
      toast.success(t('common.unpublishedSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const getStatus = (tpl: GenerationTemplate): SkillStatus => tpl.status || 'published';

  const handleUploadRefImage = async (elementType: string, file: File) => {
    if (!editingTemplate) return;
    if (!(await guardAspectRatio(file))) return;
    try {
      const res = await uploadTemplateRefImage(editingTemplate.id, elementType, file);
      const key = `${elementType === 'character' ? 'char' : elementType}ProjectRefImages` as keyof FormState;
      setForm(f => ({ ...f, [key]: res.projectRefImages }));
    } catch (err: any) {
      toast.error(err.message || t('common.uploadFailed'));
    }
  };

  const handleDeleteRefImage = async (elementType: string, url: string) => {
    if (!editingTemplate) return;
    try {
      const res = await deleteTemplateRefImage(editingTemplate.id, elementType, url);
      const key = `${elementType === 'character' ? 'char' : elementType}ProjectRefImages` as keyof FormState;
      setForm(f => ({ ...f, [key]: res.projectRefImages }));
    } catch (err: any) {
      toast.error(err.message || t('common.deleteFailed'));
    }
  };

  const handleUploadCover = async (file: File) => {
    if (!editingTemplate) return;
    if (!(await guardAspectRatio(file))) return;
    try {
      const res = await uploadTemplateCoverImage(editingTemplate.id, file);
      setForm(f => ({ ...f, coverImageUrl: res.coverImageUrl }));
      setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? { ...t, coverImageUrl: res.coverImageUrl } : t));
    } catch (err: any) {
      toast.error(err.message || t('common.uploadFailed'));
    }
  };

  const handleDeleteCover = async () => {
    if (!editingTemplate) return;
    try {
      await deleteTemplateCoverImage(editingTemplate.id);
      setForm(f => ({ ...f, coverImageUrl: '' }));
      setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? { ...t, coverImageUrl: '' } : t));
    } catch (err: any) {
      toast.error(err.message || t('common.deleteFailed'));
    }
  };

  const maxProjectRefs = MAX_TOTAL_REF_IMAGES;

  // 拖拽 + 粘贴上传区（固定 3 类参考图 + 封面），必须无条件声明在组件顶层，避免违反 Hooks 规则
  const charRefZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxProjectRefs - form.charProjectRefImages.length),
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadRefImage('character', f);
    },
  });
  const sceneRefZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxProjectRefs - form.sceneProjectRefImages.length),
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadRefImage('scene', f);
    },
  });
  const propRefZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxProjectRefs - form.propProjectRefImages.length),
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadRefImage('prop', f);
    },
  });
  const refZonesByType: Record<string, UseImageUploadZoneResult> = {
    character: charRefZone,
    scene: sceneRefZone,
    prop: propRefZone,
  };
  const coverZone = useImageUploadZone({ onFiles: (imgs) => handleUploadCover(imgs[0]) });

  const refImageSection = (
    label: string,
    elementType: string,
    images: string[],
    fileRef: React.RefObject<HTMLInputElement>,
    zone: UseImageUploadZoneResult,
  ) => {
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{t('template.projectRefImages', { count: images.length, max: maxProjectRefs })}</span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: 'relative', width: '52px', height: '52px' }}>
              <img src={tosAccelerate(url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border)' }} />
              <button
                onClick={() => handleDeleteRefImage(elementType, url)}
                style={{ position: 'absolute', top: '-6px', right: '-6px', width: '16px', height: '16px', borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >&times;</button>
            </div>
          ))}
          {editingTemplate && images.length < maxProjectRefs && (
            <button
              {...zone.zoneProps}
              onClick={() => fileRef.current?.click()}
              style={{ width: '52px', height: '52px', border: '1px dashed var(--border-hover)', borderRadius: '4px', background: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-3)', ...getDropHighlightStyle(zone.isOver) }}
            >+</button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadRefImage(elementType, f); e.target.value = ''; }} />
        {!editingTemplate && <p style={{ fontSize: '0.72rem', color: 'var(--text-3)', margin: '4px 0 0 0' }}>{t('template.saveBeforeUpload')}</p>}
      </div>
    );
  };

  const elementConfigSection = (
    label: string,
    elementType: string,
    fixedPrompt: string,
    setFixedPrompt: (s: string) => void,
    images: string[],
    fileRef: React.RefObject<HTMLInputElement>,
    helpText?: string,
  ) => (
    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem' }}>{label}{helpText && <HelpTip text={helpText} />}</h4>
      <div style={{ marginBottom: '0.5rem' }}>
        <label style={{ fontSize: '0.78rem' }}>{t('template.fixedPrompt')}<HelpTip text={t('template.fixedPromptHelp')} /></label>
        <textarea
          value={fixedPrompt}
          onChange={e => setFixedPrompt(e.target.value)}
          rows={2}
          style={{ width: '100%', fontSize: '0.8rem', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.4rem', resize: 'vertical', boxSizing: 'border-box' }}
          placeholder={DEFAULT_FIXED_PROMPT}
        />
      </div>
      {refImageSection(label, elementType, images, fileRef, refZonesByType[elementType])}
    </div>
  );

  return (
    <div className="container" style={{ padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('common.breadcrumbHome'), to: '/projects' }, { label: t('template.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('template.pageTitle')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('template.createBtn')}</button>
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {templates.length === 0 && <p style={{ color: 'var(--text-2)' }}>{t('template.empty')}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 300px))', gap: '1rem' }}>
            {templates.map(tpl => {
              const status = getStatus(tpl);
              const label = statusLabels[status];
              return (
                <div key={tpl.id} style={{ background: 'var(--surface-2)', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
                  {/* 封面图区域 */}
                  <div style={{ aspectRatio: '3/4', position: 'relative', overflow: 'hidden', background: 'var(--surface-3)' }}>
                    <img
                      src={tosAccelerate(tpl.coverImageUrl || '/template-cover-placeholder.png')}
                      alt={tpl.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <span style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '10px', color: label.color, background: label.bg }}>
                      {t(`status.${label.text}`)}
                    </span>
                    {tpl.isDefault && (
                      <span style={{ position: 'absolute', top: '8px', left: '8px', background: 'var(--primary)', color: '#fff', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '10px' }}>
                        {t('common.defaultBadge')}
                      </span>
                    )}
                  </div>
                  {/* 信息区 */}
                  <div style={{ padding: '0.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.25rem' }}>{tpl.name}</div>
                    <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
                      {tpl.jimengModel || 'jimeng-5.0'}
                    </p>
                    {tpl.reviewNote && status === 'draft' && (
                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.72rem', color: '#dc2626', background: '#fee2e2', padding: '0.25rem 0.4rem', borderRadius: '4px' }}>
                        {t('common.rejectReason')}{tpl.reviewNote}
                      </p>
                    )}
                    {/* 操作按钮 */}
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: 'auto', paddingTop: '0.5rem' }}>
                      {isOwner(tpl) && (status === 'draft' || status === 'unpublished') && (
                        <button className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => handleSubmitReview(tpl)}>{t('common.submitReview')}</button>
                      )}
                      {isOwner(tpl) && status === 'pending_review' && (
                        <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => handleWithdraw(tpl)}>{t('common.withdraw')}</button>
                      )}
                      {canModerate && status === 'pending_review' && (
                        <>
                          <button className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => handleApprove(tpl)}>{t('common.approve')}</button>
                          <button className="btn btn-danger" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => openReject(tpl)}>{t('common.reject')}</button>
                        </>
                      )}
                      {canModerate && status === 'published' && !tpl.isDefault && (
                        <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1, color: '#dc2626' }} onClick={() => handleUnpublish(tpl)}>{t('common.unpublish')}</button>
                      )}
                      {canModerate && (status === 'draft' || status === 'pending_review' || status === 'unpublished') && (
                        <button className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => handlePublish(tpl)}>
                          {status === 'unpublished' ? t('common.republish') : t('common.publish')}
                        </button>
                      )}
                      {canEdit(tpl) && (status === 'draft' || status === 'unpublished' || canModerate) && (
                        <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => openEdit(tpl)}>{t('common.edit')}</button>
                      )}
                      {canEdit(tpl) && (status !== 'published' || canModerate) && (
                        <button className="btn btn-danger" style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', flex: 1 }} onClick={() => handleDelete(tpl)} disabled={tpl.isDefault}>{t('common.delete')}</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingTemplate ? t('template.editTitle') : t('template.createTitle')}
        width="650px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('template.nameLabel')}<HelpTip text={t('template.nameHelp')} /></label>
          <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t("template.namePlaceholder")} />
        </div>

        <div className="form-group">
          <label className="form-label">{t('template.coverImage')}<HelpTip text={t('template.coverImageHelp')} /></label>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <div style={{ width: '90px', aspectRatio: '3/4', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
              <img
                src={tosAccelerate(form.coverImageUrl || '/template-cover-placeholder.png')}
                alt="cover"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {editingTemplate ? (
                <>
                  <button type="button" className="btn btn-ghost" {...coverZone.zoneProps} style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', ...getDropHighlightStyle(coverZone.isOver) }} onClick={() => coverFileRef.current?.click()}>
                    {form.coverImageUrl ? t('template.replaceCover') : t('template.uploadCover')}
                  </button>
                  {form.coverImageUrl && (
                    <button type="button" className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', color: '#dc2626' }} onClick={handleDeleteCover}>
                      {t('template.deleteCover')}
                    </button>
                  )}
                </>
              ) : (
                <p style={{ fontSize: '0.72rem', color: 'var(--text-3)', margin: 0 }}>{t('template.saveBeforeUpload')}</p>
              )}
            </div>
          </div>
          <input ref={coverFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadCover(f); e.target.value = ''; }} />
        </div>

        {activeImageModels_.length > 0 ? (() => {
          const platforms = Array.from(new Set(activeImageModels_.map(m => m.platform)));
          const modelsForPlatform = activeImageModels_.filter(m => m.platform === form.imagePlatform);
          const effectiveBasePlatform = form.baseImagePlatform || form.imagePlatform;
          const baseModelsForPlatform = activeImageModels_.filter(m => m.platform === effectiveBasePlatform);
          return (
            <>
              <div className="form-group">
                <label className="form-label">{t('template.baseImageModel')}<HelpTip text={t('template.baseImageModelHelp')} /></label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select className="input" value={form.baseImagePlatform || form.imagePlatform} onChange={e => {
                    const p = e.target.value;
                    const first = activeImageModels_.find(m => m.platform === p);
                    setForm(f => ({ ...f, baseImagePlatform: p, baseImageModel: first?.model || '' }));
                  }} style={{ flex: 1 }}>
                    {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select className="input" value={form.baseImageModel || form.imageModel} onChange={e => setForm({ ...form, baseImageModel: e.target.value })} style={{ flex: 1 }}>
                    {baseModelsForPlatform.map(m => <option key={m.model} value={m.model}>{m.displayName || m.model}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('template.stateImageModel')}<HelpTip text={t('template.stateImageModelHelp')} /></label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select className="input" value={form.imagePlatform} onChange={e => {
                    const p = e.target.value;
                    const first = activeImageModels_.find(m => m.platform === p);
                    setForm(f => ({ ...f, imagePlatform: p, imageModel: first?.model || f.imageModel, jimengModel: first?.model || f.jimengModel }));
                  }} style={{ flex: 1 }}>
                    {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select className="input" value={form.imageModel} onChange={e => setForm({ ...form, imageModel: e.target.value, jimengModel: e.target.value })} style={{ flex: 1 }}>
                    {modelsForPlatform.map(m => <option key={m.model} value={m.model}>{m.displayName || m.model}</option>)}
                  </select>
                </div>
              </div>
            </>
          );
        })() : (
          <div className="form-group">
            <label className="form-label">{t('template.imagePlatformLabel')}<HelpTip text={t('template.imagePlatformHelp')} /></label>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', padding: '0.5rem 0' }}>
              {t('template.noImageModels')}
            </div>
          </div>
        )}

        {activeVideoModels_.length > 0 && (() => {
          const platforms = Array.from(new Set(activeVideoModels_.map(m => m.platform)));
          const modelsForPlatform = activeVideoModels_.filter(m => m.platform === form.videoPlatform);
          return (
            <div className="form-group">
              <label className="form-label">{t('template.videoPlatformLabel')}<HelpTip text={t('template.videoPlatformHelp')} /></label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select className="input" value={form.videoPlatform} onChange={e => {
                  const p = e.target.value;
                  setForm(f => ({ ...f, videoPlatform: p, videoModel: activeVideoModels_.find(m => m.platform === p)?.model || f.videoModel }));
                }} style={{ flex: 1 }}>
                  {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select className="input" value={form.videoModel} onChange={e => setForm({ ...form, videoModel: e.target.value })} style={{ flex: 1 }}>
                  {modelsForPlatform.map(m => <option key={m.model} value={m.model}>{m.displayName || m.model}</option>)}
                </select>
              </div>
            </div>
          );
        })()}

        {/* 大语言模型选择 (per-role) */}
        <div className="form-group">
          <LLMRoleOverridesSection
            providers={llmProviders}
            roleOverrides={form.llmRoleOverrides}
            roles={buildLLMRoleOverrideOptions(t, 'template')}
            title={t('template.llmLabel')}
            titleTip={t('template.llmHelp')}
            globalDefaultLabel={t('template.llmGlobalDefault')}
            providerDisabledLabel={t('template.llmDisabled')}
            providerUnavailableLabel={t('template.llmUnavailable')}
            selectModelLabel={t('template.llmSelectModel')}
            emptyLabel={t('template.llmEmpty')}
            onRoleOverridesChange={update => setForm(current => ({
              ...current,
              llmRoleOverrides: typeof update === 'function' ? update(current.llmRoleOverrides) : update,
            }))}
          />
        </div>

        {elementConfigSection(t('template.charConfig'), 'character', form.charFixedPrompt, s => setForm({...form, charFixedPrompt: s}), form.charProjectRefImages, charFileRef, t('template.charConfigHelp'))}
        {elementConfigSection(t('template.sceneConfig'), 'scene', form.sceneFixedPrompt, s => setForm({...form, sceneFixedPrompt: s}), form.sceneProjectRefImages, sceneFileRef, t('template.sceneConfigHelp'))}
        {elementConfigSection(t('template.propConfig'), 'prop', form.propFixedPrompt, s => setForm({...form, propFixedPrompt: s}), form.propProjectRefImages, propFileRef, t('template.propConfigHelp'))}

        <div className="form-group">
          <label className="form-label">{t('template.charStatePrefix')}<HelpTip text={t('template.charStatePrefixHelp')} /></label>
          <textarea className="input textarea" rows={3} value={form.charStatePromptPrefix} onChange={e => setForm({ ...form, charStatePromptPrefix: e.target.value })} style={{ fontSize: '0.82rem' }} />
          <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '2px 8px', marginTop: '4px' }} onClick={() => setForm({ ...form, charStatePromptPrefix: DEFAULT_CHAR_STATE_PREFIX })}>{t('template.restoreDefault')}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '0.78rem' }}>
            <span style={{ color: '#666' }}>{t('template.outputRatio')}</span>
            {['16:9', '9:16', '1:1', '4:3', '3:4'].map(r => (
              <button
                key={r}
                type="button"
                className={form.charStateImageRatio === r ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{ fontSize: '0.72rem', padding: '2px 10px' }}
                onClick={() => setForm({ ...form, charStateImageRatio: r })}
              >{r}</button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('template.sceneStatePrefix')}<HelpTip text={t('template.sceneStatePrefixHelp')} /></label>
          <textarea className="input textarea" rows={2} value={form.sceneStatePromptPrefix} onChange={e => setForm({ ...form, sceneStatePromptPrefix: e.target.value })} placeholder={t("template.emptyPlaceholder")} style={{ fontSize: '0.82rem' }} />
          {form.sceneStatePromptPrefix && (
            <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '2px 8px', marginTop: '4px' }} onClick={() => setForm({ ...form, sceneStatePromptPrefix: '' })}>{t('template.clear')}</button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '0.78rem' }}>
            <span style={{ color: '#666' }}>{t('template.outputRatio')}</span>
            {['16:9', '9:16', '1:1', '4:3', '3:4'].map(r => (
              <button
                key={r}
                type="button"
                className={form.sceneStateImageRatio === r ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{ fontSize: '0.72rem', padding: '2px 10px' }}
                onClick={() => setForm({ ...form, sceneStateImageRatio: r })}
              >{r}</button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t("template.propStatePrefix")}<HelpTip text={t("template.propStatePrefixHelp")} /></label>
          <textarea className="input textarea" rows={2} value={form.propStatePromptPrefix} onChange={e => setForm({ ...form, propStatePromptPrefix: e.target.value })} placeholder={t("template.emptyPlaceholder")} style={{ fontSize: '0.82rem' }} />
          {form.propStatePromptPrefix && (
            <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '2px 8px', marginTop: '4px' }} onClick={() => setForm({ ...form, propStatePromptPrefix: '' })}>{t('template.clear')}</button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '0.78rem' }}>
            <span style={{ color: '#666' }}>{t('template.outputRatio')}</span>
            {['16:9', '9:16', '1:1', '4:3', '3:4'].map(r => (
              <button
                key={r}
                type="button"
                className={form.propStateImageRatio === r ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{ fontSize: '0.72rem', padding: '2px 10px' }}
                onClick={() => setForm({ ...form, propStateImageRatio: r })}
              >{r}</button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t("template.videoPrefixLabel")}<HelpTip text={t("template.videoPrefixHelp")} /></label>
          <textarea className="input textarea" rows={2} value={form.videoPrefixPrompt} onChange={e => setForm({ ...form, videoPrefixPrompt: e.target.value })} placeholder={t("template.videoPrefixPlaceholder")} style={{ fontSize: '0.82rem' }} />
        </div>

        <div className="form-group">
          <label className="form-label">{t("template.videoExtraLabel")}<HelpTip text={t("template.videoExtraHelp")} /></label>
          <textarea className="input textarea" rows={2} value={form.videoExtraPrompt} onChange={e => setForm({ ...form, videoExtraPrompt: e.target.value })} placeholder={t("template.videoExtraPlaceholder")} style={{ fontSize: '0.82rem' }} />
        </div>

        {canEditDefault && (
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="tplDefault" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
          <label htmlFor="tplDefault" style={{ margin: 0, cursor: 'pointer', fontSize: '0.85rem' }}>{t("template.setDefaultPreset")}<HelpTip text={t("template.setDefaultPresetHelp")} /></label>
        </div>
        )}

        {error && <p className="error-text">{error}</p>}
      </Modal>

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        open={!!deletingTemplate}
        onCancel={() => setDeletingTemplate(null)}
        onConfirm={confirmDelete}
        title={t("template.deleteTitle")}
        message={<>{t("template.deleteConfirm", { name: deletingTemplate?.name })}</>}
        confirmText={t("template.deleteConfirmBtn")}
        confirmDanger
      />

      {/* 驳回弹窗 */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title={t("template.rejectTitle")}
        width="450px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>{t("common.cancel")}</button>
            <button className="btn btn-danger" onClick={doReject}>{t("common.confirmReject")}</button>
          </>
        }
      >
        <p style={{ marginTop: 0, fontSize: '0.85rem' }}>{t("template.rejectPrompt", { name: rejectTarget?.name })}</p>
        <textarea
          className="input textarea"
          style={{ minHeight: '80px', width: '100%', boxSizing: 'border-box' }}
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          placeholder={t("template.rejectPlaceholder")}
        />
      </Modal>
    </div>
  );
};

export default TemplateList;
